/** Public site origin, no trailing slash — used for canonical & social URLs. */
export const SITE_URL = (import.meta.env.VITE_SITE_URL || 'https://www.pnggo.me').replace(
  /\/$/,
  ''
);

const DEFAULT_ROBOTS =
  'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

function setMetaName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setMetaProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

/** Builds absolute URL for a route path (`/` → site origin with trailing slash). */
export function canonicalUrl(path: string): string {
  const normalized =
    path === '/' || path === '' ? '/' : path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${normalized}`;
}

export type PageMetaOptions = {
  title: string;
  description: string;
  /** Route path, e.g. `/` or `/guidelines`. Used for canonical & `og:url`. */
  path: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  /** Full image URL; defaults to `${SITE_URL}/favicon.png`. */
  imageUrl?: string;
  /** Omit to use default indexable robots string. */
  robots?: string;
};

/**
 * Updates document title and matching meta/link tags for the current SPA view.
 * Call from each page's `useEffect` so crawlers and previews get route-specific metadata.
 */
export function applyPageMeta(opts: PageMetaOptions) {
  const canonical = canonicalUrl(opts.path);
  const ogTitle = opts.ogTitle ?? opts.title;
  const ogDesc = opts.ogDescription ?? opts.description;
  const twTitle = opts.twitterTitle ?? opts.title;
  const twDesc = opts.twitterDescription ?? opts.description;
  const image = opts.imageUrl ?? `${SITE_URL}/favicon.png`;

  document.title = opts.title;

  setMetaName('description', opts.description);
  if (opts.keywords !== undefined) {
    setMetaName('keywords', opts.keywords);
  }
  setMetaName('robots', opts.robots ?? DEFAULT_ROBOTS);

  setCanonical(canonical);

  setMetaProperty('og:type', opts.ogType ?? 'website');
  setMetaProperty('og:title', ogTitle);
  setMetaProperty('og:description', ogDesc);
  setMetaProperty('og:url', canonical);
  setMetaProperty('og:image', image);

  setMetaName('twitter:title', twTitle);
  setMetaName('twitter:description', twDesc);
  setMetaName('twitter:image', image);
}
